import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Plus, Layers3, LayoutGrid, Table as TableIcon,
  Edit, Trash2, Loader, Search,
  Mountain, MessageCircleQuestion, MessageCircleMore, ChevronLeft
} from 'lucide-react'
import Select from 'react-select'
import { motion, AnimatePresence } from 'framer-motion'
import Drawer from '@/layout/Drawer'
import ConfirmModal from '@/widgets/ConfirmModal'
import {
  getLessons as apiGetLessons, addLesson, updateLesson, deleteLesson
} from '@/api/kuizy'
import {
  levelMapping, examTypeMapping, typeSummaryMapping,
  formatDateTime
} from '@/settings/master'
import ViewSwitcher from '../components/ui/ViewSwitcher'

const optAll = { value: '', label: 'ทั้งหมด' }
const toFilterOpts = obj => [optAll, ...Object.entries(obj).map(([v, l]) => ({ value: v, label: l }))]
const toEnumOpts = obj => Object.entries(obj).map(([v, l]) => ({ value: v, label: l }))

export default function LessonsPage() {
  const { cid } = useParams()
  const uid = localStorage.userId || '1'

  const [rows, setRows] = useState([])
  const [loading, setLoad] = useState(false)
  const [view, setView] = useState('card')
  const [searchIn, setSI] = useState('')
  const [searchTxt, setST] = useState('')

  /* filters */
  const [lvF, setLv] = useState(optAll)
  const [exF, setEx] = useState(optAll)
  const [sumF, setSum] = useState(optAll)

  /* drawer */
  const [open, setOpen] = useState(false)
  const [isNew, setNew] = useState(false)
  const [saving, setSav] = useState(false)
  const [item, setItem] = useState(null)

  /* confirm / alert */
  const [del, setDel] = useState(null)
  const [delLoad, setDL] = useState(false)
  const [alert, setAlert] = useState({ open: false, msg: '' })

  /* fetch lessons */
  const fetchData = useCallback(async () => {
    setLoad(true)
    try {
      const { data } = await apiGetLessons({ contentId: cid })
      setRows(Array.isArray(data?.data) ? data.data : [])
    } catch { setAlert({ open: true, msg: 'โหลดบทเรียนไม่สำเร็จ' }) }
    finally { setLoad(false) }
  }, [cid])
  useEffect(() => { fetchData() }, [fetchData])

  /* filter list */
  const list = useMemo(() =>
    rows.filter(r => {
      if (searchTxt &&
        !`${r.lesson_name}${r.lesson_id}`.toLowerCase().includes(searchTxt.toLowerCase())) return false
      if (lvF.value && r.difficult_level !== lvF.value) return false
      if (exF.value && r.exam_type !== exF.value) return false
      if (sumF.value && r.type_summary !== sumF.value) return false
      return true
    }), [rows, searchTxt, lvF, exF, sumF])

  /* helpers */
  const startNew = () => {
    setItem({
      lesson_name: '',
      content_id: cid,
      difficult_level: '2',
      exam_type: 'multipleChoice',
      type_summary: 'TF',
      type_summary_min: 1,
      type_summary_max: 4
    })
    setNew(true); setOpen(true)
  }
  const startEdit = l => {
    setItem({
      lesson_id: l.lesson_id,
      lesson_name: l.lesson_name,
      content_id: l.content_id,
      difficult_level: l.difficult_level,
      exam_type: l.exam_type,
      type_summary: l.type_summary,
      type_summary_min: l.type_summary_min,
      type_summary_max: l.type_summary_max
    })
    setNew(false); setOpen(true)
  }

  const save = async () => {
    if (!item.lesson_name?.trim()) { setAlert({ open: true, msg: 'กรอกชื่อบทเรียน' }); return }
    setSav(true)
    try {
      if (isNew) {
        await addLesson({ ...item, lesson_name: item.lesson_name.trim(), created_by: uid })
      } else {
        await updateLesson({
          id: item.lesson_id,
          lesson_name: item.lesson_name.trim(),
          content_id: item.content_id,
          difficult_level: item.difficult_level,
          type_summary: item.type_summary,
          type_summary_min: item.type_summary_min,
          type_summary_max: item.type_summary_max,
          exam_type: item.exam_type
        })
      }
      fetchData(); setOpen(false)
    } catch { setAlert({ open: true, msg: 'บันทึกไม่สำเร็จ' }) }
    finally { setSav(false) }
  }

  const confirmDel = async () => {
    if (!del) return
    setDL(true)
    try { await deleteLesson(del.lesson_id); fetchData() }
    catch { setAlert({ open: true, msg: 'ลบไม่สำเร็จ' }) }
    finally { setDL(false); setDel(null) }
  }

  /* render */
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: .35 }} className="flex flex-col gap-6 p-4 sm:p-6"
    >
      {/* header */}
      <motion.header
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .35, delay: .05 }}
        className="flex flex-wrap items-end gap-3 justify-between"
      >
        <h1 onClick={fetchData}
          className="flex items-center gap-2 text-lg font-semibold cursor-pointer ">
          <Link to={`/content/`} className="text-primary font-bold text-base hover:underline mr-2">
            <ChevronLeft size={30} />
          </Link>
          <Layers3 size={20} /> จัดการบทเรียน ({rows.length})
        </h1>

        <div className="flex flex-wrap gap-2">
          <ViewSwitcher view={view} setView={setView} />
          <button onClick={startNew}
            className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm">
            <Plus size={16} /> เพิ่มบทเรียน
          </button>
        </div>
      </motion.header>

      {/* search / filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .35, delay: .05 }}
        className="flex flex-wrap items-end gap-4 bg-white rounded-xl shadow-md p-4"
      >
        {/* level */}
        <div className="flex flex-col gap-1 w-52">
          <label className="flex items-center gap-1 text-sm"><Mountain size={16} /> ระดับความยาก</label>
          <Select classNamePrefix="rs" options={toFilterOpts(levelMapping)} value={lvF}
            onChange={setLv} />
        </div>
        {/* exam type */}
        <div className="flex flex-col gap-1 w-52">
          <label className="flex items-center gap-1 text-sm"><MessageCircleQuestion size={16} /> ประเภทข้อสอบ</label>
          <Select classNamePrefix="rs" options={toFilterOpts(examTypeMapping)} value={exF}
            onChange={setEx} />
        </div>
        {/* summary type */}
        <div className="flex flex-col gap-1 w-52">
          <label className="flex items-center gap-1 text-sm"><MessageCircleMore size={16} /> ประเภทคำตอบ</label>
          <Select classNamePrefix="rs" options={toFilterOpts(typeSummaryMapping)} value={sumF}
            onChange={setSum} />
        </div>

        {/* text search */}
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
          <div className="relative">
            <input
              value={searchIn} onChange={e => setSI(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && setST(searchIn)}
              className="w-full border border-gray-300 rounded-lg px-10 py-2 text-sm focus:ring-2 focus:ring-primary"
              placeholder="ค้นหาชื่อ / รหัสบทเรียน" />
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          </div>
        </div>

        <button onClick={() => setST(searchIn)}
          className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
          <Search size={16} /> ค้นหา
        </button>
      </motion.div>

      {/* card grid */}
      {view === 'card' && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: .35, delay: .1 }}
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
        >
          {loading
            ? <div className="col-span-full flex justify-center py-12"><Loader className="animate-spin" /></div>
            : list.length === 0
              ? <div className="col-span-full text-center text-gray-500 py-12">ไม่พบบทเรียน</div>
              : list.map(l => (
                <motion.div key={l.lesson_id} whileHover={{ y: -4 }}
                  className="bg-white rounded-2xl shadow-md p-6 flex flex-col gap-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Link to={`/content/${encodeURIComponent(cid)}/lessons/${encodeURIComponent(l.lesson_id)}/bank`}
                        className="text-primary font-semibold text-base hover:underline">
                        {l.lesson_name}
                      </Link>
                    </div>
                    <button onClick={() => startEdit(l)} className="text-primary hover:text-primary/80">
                      <Edit size={18} />
                    </button>
                  </div>

                  <div className='p-4 bg-blue-50 rounded-lg text-center'>
                    <button className=''>
                      เนื้อหา
                    </button>
                  </div>

                  {/* mini-stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <Link to={`/content/${encodeURIComponent(cid)}/lessons/${encodeURIComponent(l.lesson_id)}/bank`}
                      className="bg-primary/10 rounded-lg p-3 flex flex-col items-center hover:bg-neutral-200">
                      <LayoutGrid size={18} className="text-secondary" />
                      <span className="text-[10px] text-gray-600 mt-1">คลังข้อสอบ</span>
                      <strong className="text-lg">{l.question_count} ข้อ</strong>
                    </Link>
                    <div className="bg-neutral-100 rounded-lg p-3 flex flex-col items-center">
                      <Mountain size={18} className="text-secondary" />
                      <span className="text-[10px] text-gray-600 mt-1">ระดับความยาก</span>
                      <strong className="text-md">{levelMapping[l.difficult_level]}</strong>
                    </div>
                    <div className="bg-neutral-100 rounded-lg p-3 flex flex-col items-center">
                      <MessageCircleQuestion size={18} className="text-secondary" />
                      <span className="text-[10px] text-gray-600 mt-1">ประเภทข้อสอบ</span>
                      <strong className="text-md text-center">{examTypeMapping[l.exam_type]}</strong>
                    </div>
                    <div className="bg-neutral-100 rounded-lg p-3 flex flex-col items-center">
                      <MessageCircleMore size={18} className="text-secondary" />
                      <span className="text-[10px] text-gray-600 mt-1">ประเภทคำตอบ</span>
                      <strong className="text-md text-center">{typeSummaryMapping[l.type_summary]}</strong>
                    </div>
                  </div>

                  {/* dates & actions */}
                  <div className="flex items-end justify-between">
                    <div className="text-xs text-gray-500">
                      <p>สร้างเมื่อ {formatDateTime(l.created_at)}</p>
                      <p>แก้ไขล่าสุด {formatDateTime(l.updated_at || l.created_at)}</p>
                    </div>
                    <button onClick={() => setDel(l)} className="text-red-600 hover:text-red-500">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </motion.div>
              ))}
        </motion.div>
      )}

      {/* table */}
      {view === 'table' && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: .35, delay: .1 }}
          className="bg-white rounded-xl shadow-md overflow-x-auto"
        >
          <table className="min-w-[880px] w-full text-sm">
            <thead className="bg-gray-100 text-gray-600 whitespace-nowrap">
              <tr>
                <th className="py-2 px-3 w-14 text-left">#</th>
                <th className="py-2 px-3 text-left">บทเรียน</th>
                <th className="py-2 px-3 text-right">คลังข้อสอบ</th>
                <th className="py-2 px-3 text-right">ระดับความยาก</th>
                <th className="py-2 px-3 text-right">ประเภทข้อสอบ</th>
                <th className="py-2 px-3 text-right">ประเภทคำตอบ</th>
                <th className="py-2 px-3 text-right hidden lg:table-cell">สร้างเมื่อ</th>
                <th className="py-2 px-3 text-right hidden lg:table-cell">แก้ไขล่าสุด</th>
                <th className="py-2 px-3 w-24 text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? <tr><td colSpan={9} className="py-6 text-center"><Loader className="animate-spin" /></td></tr>
                : list.length === 0
                  ? <tr><td colSpan={9} className="py-6 text-center text-gray-500">ไม่พบบทเรียน</td></tr>
                  : list.map((l, i) => (
                    <tr key={l.lesson_id} className="odd:bg-white even:bg-gray-50 whitespace-nowrap">
                      <td className="py-2 px-3">{i + 1}</td>
                      <td className="py-2 px-3">
                        <Link to={`/content/${encodeURIComponent(cid)}/lessons/${encodeURIComponent(l.lesson_id)}/bank`}
                          className="text-primary hover:underline">{l.lesson_name}</Link>
                      </td>
                      <td className="py-2 px-3 text-right">{l.question_count}</td>
                      <td className="py-2 px-3 text-right">{levelMapping[l.difficult_level]}</td>
                      <td className="py-2 px-3 text-right">{examTypeMapping[l.exam_type]}</td>
                      <td className="py-2 px-3 text-right">{typeSummaryMapping[l.type_summary]}</td>
                      <td className="py-2 px-3 text-right hidden lg:table-cell">{formatDateTime(l.created_at)}</td>
                      <td className="py-2 px-3 text-right hidden lg:table-cell">{formatDateTime(l.updated_at || l.created_at)}</td>
                      <td className="py-2 px-3 text-right space-x-2">
                        <button onClick={() => startEdit(l)} className="text-primary hover:text-primary/80"><Edit size={16} /></button>
                        <button onClick={() => setDel(l)} className="text-red-600 hover:text-red-500"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </motion.div>
      )}

      {/* drawer */}
      <Drawer open={open} onClose={() => setOpen(false)}
        title={`${isNew ? 'เพิ่ม' : 'แก้ไข'}บทเรียน`}>
        {item && (
          <form onSubmit={e => { e.preventDefault(); save() }} className="flex flex-col gap-3">
            <label className="flex items-center gap-1 text-sm"><Layers3 size={14} /> ชื่อบทเรียน <span className="text-red-500 font-bold">*</span></label>
            <input value={item.lesson_name}
              onChange={e => setItem({ ...item, lesson_name: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />

            <label className="flex items-center gap-1 text-sm"><Mountain size={14} /> ระดับความยาก <span className="text-red-500 font-bold">*</span></label>
            <Select
              classNamePrefix="rs"
              isClearable={false}
              options={toEnumOpts(levelMapping)}
              value={toEnumOpts(levelMapping).find(o => o.value === item.difficult_level)}
              onChange={o => setItem({ ...item, difficult_level: o.value })}
            />

            <label className="flex items-center gap-1 text-sm"><MessageCircleQuestion size={14} /> ประเภทข้อสอบ <span className="text-red-500 font-bold">*</span></label>
            <Select
              classNamePrefix="rs"
              isClearable={false}
              options={toEnumOpts(examTypeMapping)}
              value={toEnumOpts(examTypeMapping).find(o => o.value === item.exam_type)}
              onChange={o => setItem({ ...item, exam_type: o.value })}
            />

            <label className="flex items-center gap-1 text-sm"><MessageCircleMore size={14} /> ประเภทคำตอบ <span className="text-red-500 font-bold">*</span></label>
            <Select
              classNamePrefix="rs"
              isClearable={false}
              options={toEnumOpts(typeSummaryMapping)}
              value={toEnumOpts(typeSummaryMapping).find(o => o.value === item.type_summary)}
              onChange={o => setItem({ ...item, type_summary: o.value })}
            />

            <button disabled={saving}
              className="bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 mt-2 disabled:opacity-50">
              {saving ? <Loader className="animate-spin" size={16} /> : 'บันทึก'}
            </button>
          </form>
        )}
      </Drawer>

      {/* confirm & alert */}
      <AnimatePresence>
        {del &&
          <ConfirmModal type="confirm" title="ยืนยันการลบ"
            message={`ลบ "${del.lesson_name}" ?`}
            onConfirm={confirmDel} onCancel={() => setDel(null)} confirmDisabled={delLoad} />}
        {alert.open &&
          <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg}
            onClose={() => setAlert({ open: false, msg: '' })} />}
      </AnimatePresence>
    </motion.section>
  )
}
